import { createSelector } from 'reselect';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import { pathConstructor } from '@/hooks/useCompany/utils';
import {
  toCurrencyTruncated,
  toPercentage,
  toFactor,
  toCurrency,
} from '@/utilities/formatting';

import {
  getAnalysis,
  getFormatting,
  getSummary,
  getValue,
} from '@/pages/CompanyReport/redux/selectors';
import { getFutureSelector } from '@/pages/CompanyReport/components/Future/redux/selectors';
import { formatterFactor } from '@/utilities/format';
import { getMultipleFullName } from './utils';
import { getAveragesSelector } from '@/redux/averages/selectors';
import { shallowEqual, useSelector } from 'react-redux';

interface ReduxState {
  company: CompanyState;
  location: LocationState;
}

export type UserPeerSelection = 'saved' | 'default' | 'edit';

export type PreferredMultiple = 'PE' | 'PS' | 'PB';

interface Value {
  marketName: string;
  pe: number;
  pb: number;
  mpe: number;
  mpb: number;
  ipe: number;
  ipb: number;
  peg: number;
  isFund: boolean;
  score: number;
  companyId: string;
  uniqueSymbol: string;
  industryName: string;
  companyName: string;
  tickerSymbol: string;
  intrinsicValue: number;
  sharePrice: number;
  intrinsicDiscount: number;
  intrinsicValueFormatted: string;
  intrinsicDiscountFormatted: string;
  sharePriceFormatted: string;
  keyMetric: string;
  keyMetricTitle: string;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getValuekeyMetric = (
  intrinsicDiscount: number,
  pe: number,
  pb: number
): KeyMetric => {
  let value = '';
  let title = '';
  if (intrinsicDiscount > 0 && intrinsicDiscount < 0.5) {
    value = toPercentage(intrinsicDiscount, 1);
    title = 'Undervalued compared to fair value';
  } else if (intrinsicDiscount > 0.5) {
    value = '>50%';
    title = 'Undervalued compared to fair value';
  } else if (pe > 0) {
    value = toFactor(pe, 2);
    title = 'Price to Earnings (PE) ratio';
  } else if (pb > 0) {
    value = toFactor(pb, 2);
    title = 'Price to Book (PB) ratio';
  }
  return {
    value,
    title,
  };
};

const getValueSelector = createSelector(
  [getSummary, getValue, getFormatting, getAveragesSelector],
  (summary, valueNext, formatting, averages): Value => {
    const valueScore = summary.score.value;
    const valuekeyMetric = getValuekeyMetric(
      valueNext.intrinsicDiscount,
      valueNext.PE,
      valueNext.PB
    );

    const sharePriceFormatted = toCurrency(
      valueNext.sharePrice,
      2,
      formatting.tradingItemCurrencyISO
    );
    const intrinsicValueFormatted = toCurrency(
      valueNext.intrinsicValue,
      2,
      formatting.tradingItemCurrencyISO
    );
    const intrinsicDiscountFormatted = toPercentage(
      Math.abs(valueNext.intrinsicDiscount),
      1
    );
    const { getIndustryAverage, getMarketAverage } = averages;
    return {
      marketName: getMarketAverage('PE').source,
      pe: valueNext.PE,
      pb: valueNext.PB,
      mpe: getMarketAverage('PE').value,
      mpb: getMarketAverage('PB').value,
      ipe: getIndustryAverage('PE').value,
      ipb: getIndustryAverage('PB').value,
      peg: valueNext.PEG,
      score: valueScore,
      isFund: summary.isFund,
      companyId: summary.companyId,
      sharePrice: valueNext.sharePrice,
      sharePriceFormatted,
      companyName: summary.shortName,
      tickerSymbol: summary.tickerSymbol,
      industryName: summary.industryData.secondaryName,
      uniqueSymbol: summary.uniqueSymbol,
      intrinsicValue: valueNext.intrinsicValue,
      intrinsicValueFormatted,
      keyMetric: valuekeyMetric.value,
      keyMetricTitle: valuekeyMetric.title,
      intrinsicDiscount: valueNext.intrinsicDiscount,
      intrinsicDiscountFormatted,
    };
  }
);
export function selectDcfData(state: ReduxState) {
  const path = pathConstructor(state.location);
  const companyId = state.company.urlToIdMapping[path];
  const dcfData = getValueSelector(state, { companyId });
  return {
    companyId,
    companyName: dcfData.companyName,
    tickerSymbol: dcfData.tickerSymbol,
    sharePrice: dcfData.sharePrice,
    sharePriceFormatted: dcfData.sharePriceFormatted,
    intrinsicValue: dcfData.intrinsicValue,
    intrinsicValueFormatted: dcfData.intrinsicValueFormatted,
    intrinsicDiscount: dcfData.intrinsicDiscount,
    intrinsicDiscountFormatted: dcfData.intrinsicDiscountFormatted,
    peg: dcfData.peg,
  };
}

function selector(state: ReduxState) {
  const getOtherMultiple = (
    firstMultiple: string,
    secondMultiple: string
  ): string => {
    const multiples = [firstMultiple, secondMultiple];
    if (!multiples.includes('PE')) return 'PE';
    if (!multiples.includes('PS')) return 'PS';
    return 'PB';
  };
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const summary = state.company.summary[companyId];
    const analysis = state.company.analysis[companyId];
    const formatting = state.company.formatting[companyId];
    const {
      revenueGrowthAnnualIndustryFormatted,
      netIncomeGrowthAnnualIndustryFormatted,
    } = getFutureSelector(state, { companyId });
    const { sharePrice, sharePriceFormatted } = getValueSelector(state, {
      companyId,
    });
    //multiples
    const firstMultiple = analysis.preferredMultiple?.toUpperCase() || '';
    const secondMultiple =
      analysis.preferredMultipleSecondary?.toUpperCase() ||
      getOtherMultiple(firstMultiple, '');
    const thirdMultiple = getOtherMultiple(firstMultiple, secondMultiple);
    const pb = formatterFactor(analysis.PB);
    const pe = formatterFactor(analysis.PE);
    const ps = formatterFactor(analysis.PS);
    const multipleValues = { PE: pe, PS: ps, PB: pb };
    const multipleValuesRaw = {
      PE: analysis.PE,
      PS: analysis.PS,
      PB: analysis.PB,
    };
    const earningsPerShare1yFormatted = toCurrencyTruncated(
      analysis.earningsPerShare1y,
      2,
      formatting.reportingCurrencyISO
    );
    const multiples = {
      preferredMultiple: {
        name: firstMultiple as PreferredMultiple,
        fullName: getMultipleFullName(firstMultiple as PreferredMultiple),
        value: multipleValues[firstMultiple] as string,
        valueRaw: multipleValuesRaw[firstMultiple] as number,
      },
      secondMultiple: {
        name: secondMultiple as PreferredMultiple,
        fullName: getMultipleFullName(secondMultiple as PreferredMultiple),
        value: multipleValues[secondMultiple] as string,
        valueRaw: multipleValuesRaw[secondMultiple] as number,
      },
      thirdMultiple: {
        name: thirdMultiple as PreferredMultiple,
        fullName: getMultipleFullName(thirdMultiple as PreferredMultiple),
        value: multipleValues[thirdMultiple] as string,
        valueRaw: multipleValuesRaw[thirdMultiple] as number,
      },
    };
    //company stats
    const marketCap = analysis.marketCapRawData.reported;
    const formattedMarketCap = toCurrencyTruncated(
      marketCap,
      2,
      formatting.reportingCurrencyISO
    );
    const earnings = analysis.pastEarnings * analysis.pastEarningsUnit;
    const formattedEarnings = toCurrencyTruncated(
      earnings,
      2,
      formatting.reportingCurrencyISO
    );
    const sales = analysis.pastRevenue * analysis.pastRevenueUnit;
    const formattedSales = toCurrencyTruncated(
      sales,
      2,
      formatting.reportingCurrencyISO
    );
    const bookValue = analysis.pastBookValue * analysis.pastBookValueUnit;
    const formattedBookValue = toCurrencyTruncated(
      bookValue,
      2,
      formatting.reportingCurrencyISO
    );
    //Forecasts
    const forecastEarningsSign =
      analysis.forecastNetIncomeGrowth1y > 0 ? '+' : '';
    const forecastEarningsGrowth = analysis.forecastNetIncomeGrowth1y
      ? forecastEarningsSign + toPercentage(analysis.forecastNetIncomeGrowth1y)
      : 'n/a';
    const forecastSales = analysis.forecastSales * 1_000_000;
    const formattedForecastSales = toCurrencyTruncated(
      forecastSales,
      2,
      formatting.reportingCurrencyISO
    );
    const forecastSalesSign = analysis.forecastRevenueGrowth1y > 0 ? '+' : '';
    const forecastSalesGrowth = analysis.forecastRevenueGrowth1y
      ? forecastSalesSign + toPercentage(analysis.forecastRevenueGrowth1y)
      : 'n/a';

    const metricsData = {
      PE: {
        label: 'Earnings',
        value: earnings,
        formattedValue: formattedEarnings,
        secondaryValue: marketCap,
        formattedSecondaryValue: formattedMarketCap,
      },
      PS: {
        label: 'Sales',
        value: sales,
        formattedValue: formattedSales,
        secondaryValue: marketCap,
        formattedSecondaryValue: formattedMarketCap,
      },
      PB: {
        label: 'Book',
        value: bookValue,
        formattedValue: formattedBookValue,
        secondaryValue: marketCap,
        formattedSecondaryValue: formattedMarketCap,
      },
      'Forward PE': {
        label: 'Forecasted Earnings in 12m',
        value: sharePrice,
        formattedValue: sharePriceFormatted,
        formattedGrowth: forecastEarningsGrowth,
        secondaryValue: analysis.earningsPerShare1y,
        formattedSecondaryValue: earningsPerShare1yFormatted,
      },
      'Forward PS': {
        label: 'Forecasted Sales in 12m',
        value: forecastSales,
        formattedValue: formattedForecastSales,
        formattedGrowth: forecastSalesGrowth,
        secondaryValue: marketCap,
        formattedSecondaryValue: formattedMarketCap,
      },
    } as const;
    return {
      companyId,
      countryISO: summary.countryISO,
      currencyISO: formatting.tradingItemCurrencyISO,
      tickerSymbol: summary.tickerSymbol,
      uniqueSymbol: summary.uniqueSymbol,
      companyName: summary.shortName,
      preferredMultipleReason: analysis.preferredMultipleReason,
      canonicalUrl: summary.canonicalUrl,
      multiples,
      marketCap,
      metricsData,
      formattedMarketCap,
      forwardPE: formatterFactor(analysis.forwardPE),
      forwardPS: formatterFactor(analysis.forwardPS),
      valueEbitdaRatio: formatterFactor(analysis.valueEbitdaRatio),
      valueRevenueRatio: formatterFactor(analysis.valueRevenueRatio),
      PEG: formatterFactor(analysis.PEG),
      industrySecondaryName: summary.industryData.secondaryName,
      revenueGrowthAnnualIndustryFormatted,
      netIncomeGrowthAnnualIndustryFormatted,
      preferredMultipleFairRatio: analysis.preferredMultipleFairRatio || 0,
      preferredMultipleAveragePeerValue:
        analysis.preferredMultipleAveragePeerValue,
      formattedPreferredMultipleFairRatio: formatterFactor(
        analysis.preferredMultipleFairRatio
      ),
      shouldShowAptCalc: analysis.shouldShowAptCalc,
    };
  } catch (e) {
    /** means it's loading */
    return {
      companyId: '',
      countryISO: '',
      tickerSymbol: '',
      uniqueSymbol: '',
      companyName: '',
      preferredMultipleReason: '',
      multiples: {
        preferredMultiple: {
          name: 'PE' as PreferredMultiple,
          fullName: getMultipleFullName('PE'),
          value: '0x',
          valueRaw: 0,
        },
        secondMultiple: {
          name: 'PS' as PreferredMultiple,
          fullName: getMultipleFullName('PS'),
          value: '0x',
          valueRaw: 0,
        },
        thirdMultiple: {
          name: 'PB' as PreferredMultiple,
          fullName: getMultipleFullName('PB'),
          value: '0x',
          valueRaw: 0,
        },
      },
      marketCap: 0,
      metricsData: {},
      formattedMarketCap: 'n/a',
      bookValue: 0,
      formattedBookValue: 'n/a',
      forwardPE: '0x',
      forwardPS: '0x',
      preferredMultipleFairRatio: 0,
      formattedPreferredMultipleFairRatio: 'n/a',
      preferredMultipleAveragePeerValue: null,
      score: 0,
      industrySecondaryName: '',
      revenueGrowthAnnualIndustryFormatted: 'n/a',
      netIncomeGrowthAnnualIndustryFormatted: 'n/a',
      currencyISO: '',
      shouldShowAptCalc: false,
    };
  }
}

export const MultipleVsIndustryPropsFromState = createSelector(
  [getSummary, getAnalysis, getAveragesSelector, getFormatting],
  (summary, analysis) => {
    const getOtherMultiple = (
      firstMultiple: string,
      secondMultiple: string
    ): string => {
      const multiples = [firstMultiple, secondMultiple];
      if (!multiples.includes('PE')) return 'PE';
      if (!multiples.includes('PS')) return 'PS';
      return 'PB';
    };

    //multiples
    const firstMultiple = analysis.preferredMultiple?.toUpperCase() || '';
    const secondMultiple =
      analysis.preferredMultipleSecondary?.toUpperCase() ||
      getOtherMultiple(firstMultiple, '');
    const thirdMultiple = getOtherMultiple(firstMultiple, secondMultiple);
    const pb = formatterFactor(analysis.PB);
    const pe = formatterFactor(analysis.PE);
    const ps = formatterFactor(analysis.PS);
    const multipleValues = { PE: pe, PS: ps, PB: pb };
    const multipleValuesRaw = {
      PE: analysis.PE,
      PS: analysis.PS,
      PB: analysis.PB,
    };
    const multiples = {
      preferredMultiple: {
        name: firstMultiple as PreferredMultiple,
        fullName: getMultipleFullName(firstMultiple as PreferredMultiple),
        value: multipleValues[firstMultiple] as string,
        valueRaw: multipleValuesRaw[firstMultiple] as number,
      },
      secondMultiple: {
        name: secondMultiple as PreferredMultiple,
        fullName: getMultipleFullName(secondMultiple as PreferredMultiple),
        value: multipleValues[secondMultiple] as string,
        valueRaw: multipleValuesRaw[secondMultiple] as number,
      },
      thirdMultiple: {
        name: thirdMultiple as PreferredMultiple,
        fullName: getMultipleFullName(thirdMultiple as PreferredMultiple),
        value: multipleValues[thirdMultiple] as string,
        valueRaw: multipleValuesRaw[thirdMultiple] as number,
      },
    };
    return {
      multiples: multiples,
      tickerSymbol: summary.tickerSymbol,
      uniqueSymbol: summary.uniqueSymbol,
      canonicalUrl: summary.canonicalUrl,
    };
  }
);

export default selector;

export const useValuationData = () => useSelector(selector, shallowEqual);
